import debugDependency from 'debug';
import faker from 'faker';
import axios from 'axios';
import Cookie from 'js-cookie';

// TODO(bundle): check this import, might be bundling the entire products-fe app
import * as MOET_CONSTANTS from 'moet-core/src/constants';

import { DEFAULT_MERCHANT } from './constants';
import { getPreAssessmentWithSteps } from '../fixtures/pre-assessment';
import { merchantWidgetSessionUrls } from './url-config';
import {
  DEBUG_LEVELS,
  debugBuilder,
  MERCHANT_PORTAL_WIDGET_UTILS_NAMESPACE,
  setDebugMode,
} from '../tools/debug';

const { WIDGET_TYPE } = MOET_CONSTANTS;
const debug = debugBuilder(debugDependency)(MERCHANT_PORTAL_WIDGET_UTILS_NAMESPACE);

const retry = async (retries, call) => {
  for (let i = 1; i <= retries; i++) {
    try {
      return await call();
    } catch (error) {
      if (i === retries) {
        throw error;
      }
    }
  }
};

/* We do this to avoid the js-cookie library to escape quotes when stringifying JSON */
const jsCookie = Cookie.withConverter({
  read: value => value,
  write: value => value,
});

export default class MerchantWidgetSessionUtils {
  constructor(environment, region, type, verbose = true) {
    const logLevel = verbose ? DEBUG_LEVELS.LOG : DEBUG_LEVELS.INFO;
    setDebugMode(debugDependency)(debug, logLevel);

    this.environment = environment || 'local';
    this.region = region || 'eu';
    this.type = type || WIDGET_TYPE.NATIVE_KYC;

    this.prepareHttpHeaders = this.prepareHttpHeaders.bind(this);
    this.getUrl = this.getUrl.bind(this);
    this.createWidgetMerchant = this.createWidgetMerchant.bind(this);
    this.updateMerchant = this.updateMerchant.bind(this);
  }

  determineBaseUrl(options) {
    return process.env.NODE_ENV === 'development'
      ? this.getUrl('api')
      : options.overrideUrl || this.getUrl('api');
  }

  prepareHttpHeaders(headers = {}) {
    const commonHeaders = {
      accept: 'application/json',
      'Content-Type': 'application/json',
      'Klarna-Correlation-Id': faker.datatype.uuid(),
      'Klarna-Originator-Source': 'moet-e2e-test-merchant-widget-session',
    };
    return { ...commonHeaders, ...headers };
  }

  getUrl(name, path = '') {
    return `${merchantWidgetSessionUrls[this.environment][this.region][name]}${path}`;
  }

  async getWidgetUrl(merchant, params, options = {}) {
    const fetchWidgetResult = await this.fetchWidgetURL(merchant, params, options);

    const { partnerAPIToken, responseParams } = fetchWidgetResult;

    const baseUrl = this.determineBaseUrl(options);

    return `${baseUrl}/v2/widget/${partnerAPIToken}?${responseParams}`;
  }

  getMerchantFromCurrentSession(merchantForTest) {
    const merchantFromSession = JSON.parse(jsCookie.get(DEFAULT_MERCHANT) || 'null');
    if (!merchantFromSession) {
      debug.log(`MerchantWidgetSessionUtils:: no previous merchant found on cookies`);
      return null;
    }
    if (merchantFromSession && merchantFromSession.country === merchantForTest.country) {
      debug.log(
        `MerchantWidgetSessionUtils:: found merchant with MID: ${merchantFromSession.id}`,
        merchantFromSession,
      );
      return merchantFromSession;
    }
    debug.log(`MerchantWidgetSessionUtils:: found merchant with different settings`);
    return null;
  }

  defineMerchantForWidget(options, type) {
    function getEmail() {
      const firstName = faker.name.firstName();
      const lastName = faker.name.lastName();
      const label = Date.now();
      const suffix = [WIDGET_TYPE.MANAGED_KYC, WIDGET_TYPE.MANAGED_SME_KYC].includes(type)
        ? '+fakepreassessmentapproved'
        : '';

      return `${firstName}.${lastName}+${label}${suffix}@example.com`;
    }

    const {
      prefillInformation,
      addPrefill,
      convertToSnakeCase = true,
      merchantForTest = {},
    } = options;

    const email = options.email || getEmail();
    const {
      platform = 'magento',
      country = 'DE',
      // eslint-disable-next-line camelcase
      pricePlan = 'DEFAULT',
    } = merchantForTest;

    const merchantDefaultProps = {
      country,
      email,
      pricePlan,
      confirmationEmail: email,
      prefillInformation,
      platform,
    };

    debug.log(`creating new merchant with options:`, merchantDefaultProps);

    return this.getMerchantDetailsForWidget({
      ...options,
      merchantDefaultProps,
      type,
      addPrefill,
      convertToSnakeCase,
      merchantForTest: {
        ...options.merchantForTest,
        email,
        confirmationEmail: email,
      },
    });
  }

  getMerchantDetailsForWidget({
    merchantForTest,
    merchantDefaultProps,
    type = '',
    addPrefill = false,
    convertToSnakeCase = true,
  }) {
    const { country } = merchantDefaultProps;

    if (type === WIDGET_TYPE.MANAGED_RISK) {
      const {
        prefillInformation: prefillInformationFromFixture,
      } = getPreAssessmentWithSteps(country, ['prefillInformation']);
      const prefillInformation =
        merchantDefaultProps.prefillInformation || prefillInformationFromFixture;

      return {
        ...merchantDefaultProps,
        ...(addPrefill
          ? {
              prefillInformation: {
                ...prefillInformation,
                registrationCountry: country,
              },
            }
          : {}),
      };
    } else {
      return { ...merchantDefaultProps, ...merchantForTest };
    }
  }

  /**
   * `overrideUrl` cross-region URLs need to go through MPP proxy. GUI and Flow Visualiser
   * should get the correct backendUrl through MPP selectors and override the baseUrl
   */
  async createWidgetMerchant(merchant, params, payload, options) {
    const { skipFakeTransactions } = params;
    const urlSearchParams = new URLSearchParams({ skipFakeTransactions });

    const headers = this.prepareHttpHeaders();
    const baseUrl = this.determineBaseUrl(options);

    const body = {
      merchant,
      frontendType: payload.widgetType,
      contractOnboardingType: payload.contractOnboardingType,
    };

    const { merchantId: id } = await retry(3, () =>
      axios.post(`${baseUrl}/v1/tests/createWidgetMerchant?${urlSearchParams}`, body, { headers }),
    )
      .then(resp => {
        debug.info(`createWidgetMerchant: mid created ${resp.data.merchantId}`);
        return resp.data;
      })
      .catch(error => {
        debug.error('createWidgetMerchant: no mid created %o', error);
        throw error;
      });

    return { ...body.merchant, id };
  }

  async fetchWidgetURL(merchant, { widgetType, ...params }, options) {
    const headers = this.prepareHttpHeaders();
    const baseUrl = this.determineBaseUrl(options);

    const urlSearchParams = new URLSearchParams({
      ...params,
      type: widgetType,
      country: merchant.country,
    });

    const res = await axios
      .get(`${baseUrl}/v1/tests/getWidgetUrl/${merchant.id}?${urlSearchParams}`, { headers })
      .then(resp => resp.data)
      .catch(error => {
        debug.error('fetchWidgetURL: no mid created %o', error);
        return error;
      });

    // Last Element
    const tokenAndParams = res.split('/').pop();
    const [partnerAPIToken, responseParams] = tokenAndParams.split('?');

    return { merchant, partnerAPIToken, responseParams };
  }

  async createWidgetSession(session) {
    const headers = this.prepareHttpHeaders();
    const { partnerAPIToken } = session;
    const url = this.getUrl('api', `/v2/widget/${partnerAPIToken}`);

    await axios.get(url, { headers }).catch(error => {
      debug.error('createWidgetSession: no mid created %o', error);
      return error;
    });

    return session;
  }

  async updateMerchant(merchant, onboardingType, options = {}) {
    const headers = this.prepareHttpHeaders();
    const baseUrl = this.determineBaseUrl(options);
    const params = new URLSearchParams({ onboarding_type: onboardingType });

    const updatedMerchant = await axios
      .put(`${baseUrl}/v1/tests/updateMerchant?${params}`, merchant, { headers })
      .then(resp => resp.data)
      .catch(error => error);

    return updatedMerchant;
  }

  async postPreAssessmentToCache(preAssessmentDetails, options) {
    const headers = this.prepareHttpHeaders();
    const merchant = this.getMerchantFromCurrentSession(options);

    return await axios
      .post(
        this.getUrl('api', `/v2/merchants/${merchant.id}/pre-assessment`),
        preAssessmentDetails,
        {
          headers,
        },
      )
      .then(resp => resp.data)
      .catch(error => {
        debug.error('postPreAssessmentToCache: %o', error);
        return error;
      });
  }

  async getMerchant(mid) {
    const url = `${this.getUrl('api')}/v2/merchants/${mid}`;
    const headers = this.prepareHttpHeaders();
    const merchant = await axios
      .get(url, { headers })
      .then(({ data }) => data)
      .catch(error => {
        debug.error('getMerchant: %o', error);
        return error;
      });

    return merchant;
  }
}
